/**
 * SalonsView
 *
 * Ops triage: every non-removed salon classified into a status bucket so we
 * can see live customers, conversion targets, re-engagement candidates, and
 * in-flight onboarding all in one view.
 *
 * Status buckets:
 *   live-transacting  — active, using Beu payments
 *   off-platform      — active with bookings but taking payments elsewhere
 *   inactive          — has booked before, nothing recent
 *   onboarding        — signed up <30d ago, no bookings yet (active setup)
 *   stalled           — signed up 30–180d ago, still no bookings (chase)
 *   dormant           — signed up >180d ago, still no bookings (write-off)
 */

import React, { Component } from 'react';
import { adminService } from '../../../_services/admin.service';

const fmtInt = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-GB');
const fmtDate = (d) => {
    if (!d) return '—';
    const dd = new Date(d);
    return dd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const daysAgo = (d) => {
    if (!d) return '—';
    const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.round(days / 30)}mo ago`;
    return `${Math.round(days / 365)}y ago`;
};

const STATUS_META = {
    'live-transacting': { label: 'Live', description: 'Booking + using Beu payments', tone: 'ok' },
    'off-platform':     { label: 'Off-platform', description: 'Booking but 0% Beu payments', tone: 'warn' },
    'inactive':         { label: 'Inactive', description: 'No bookings in last 30 days', tone: 'neutral' },
    'onboarding':       { label: 'Onboarding', description: 'Signed up <30 days ago, no bookings yet', tone: 'info' },
    'stalled':          { label: 'Stalled', description: 'Signed up 30–180 days ago, no bookings', tone: 'warn' },
    'dormant':          { label: 'Dormant', description: 'Signed up >180 days ago, never booked', tone: 'danger' }
};

const FILTER_ORDER = ['all', 'live-transacting', 'off-platform', 'inactive', 'onboarding', 'stalled', 'dormant'];

class SalonsView extends Component {
    state = { loading: true, error: null, data: null, filter: 'all', search: '', sortKey: 'bookingsLast30', sortDir: 'desc' };

    componentDidMount() { this.fetch(); }

    fetch = async () => {
        this.setState({ loading: true, error: null });
        try {
            const res = await adminService.getFinancialsSalons();
            if (res.statusCode === 1) {
                this.setState({ data: res.responseData, loading: false });
            } else {
                this.setState({ error: res.message || 'Failed to load salons', loading: false });
            }
        } catch (err) {
            console.error('financials salons error', err);
            this.setState({ error: String(err), loading: false });
        }
    };

    setSort = (key) => {
        this.setState(s => ({ sortKey: key, sortDir: s.sortKey === key && s.sortDir === 'desc' ? 'asc' : 'desc' }));
    };

    exportCsv = () => {
        const { data, filter, search } = this.state;
        const rows = this.getFilteredSorted(data?.salons || [], filter, search);
        const lines = ['Salon,Status,Signed up,Age days,Hairdressers,Bookings all time,Bookings last 30d,Last booking'];
        rows.forEach(r => {
            const vals = [
                (r.salonName || '').replace(/,/g, ' '),
                STATUS_META[r.status]?.label || r.status,
                fmtDate(r.createdTime),
                r.ageDays ?? '',
                r.hairdresserCount,
                r.totalBookings,
                r.bookingsLast30,
                r.lastBooking ? fmtDate(r.lastBooking) : ''
            ];
            lines.push(vals.join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `beu-salons-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    getFilteredSorted(salons, filter, search) {
        const q = search.trim().toLowerCase();
        let filtered = filter === 'all' ? salons : salons.filter(s => s.status === filter);
        if (q) filtered = filtered.filter(s => (s.salonName || '').toLowerCase().includes(q));
        const { sortKey, sortDir } = this.state;
        return [...filtered].sort((a, b) => {
            let av = a[sortKey];
            let bv = b[sortKey];
            if (av instanceof Date || (typeof av === 'string' && /\d{4}-\d{2}-\d{2}/.test(av))) {
                av = new Date(av).getTime();
                bv = new Date(bv || 0).getTime();
            }
            av = av == null ? -Infinity : av;
            bv = bv == null ? -Infinity : bv;
            if (av === bv) return 0;
            const order = av > bv ? 1 : -1;
            return sortDir === 'desc' ? -order : order;
        });
    }

    renderSummary(summary, total) {
        if (!summary) return null;
        const tiles = FILTER_ORDER.filter(k => k !== 'all').map(k => {
            const meta = STATUS_META[k];
            return {
                key: k,
                label: meta.label,
                value: fmtInt(summary[k] || 0),
                sub: meta.description,
                tone: meta.tone
            };
        });
        return (
            <div className="fin-kpi-strip">
                <div className="fin-kpi-tile">
                    <div className="fin-kpi-tile-label">Total salons</div>
                    <div className="fin-kpi-tile-value">{fmtInt(total)}</div>
                    <div className="fin-kpi-tile-sub">Registered (not removed)</div>
                </div>
                {tiles.map(t => (
                    <div key={t.key} className="fin-kpi-tile">
                        <div className="fin-kpi-tile-label">{t.label}</div>
                        <div className="fin-kpi-tile-value">{t.value}</div>
                        <div className="fin-kpi-tile-sub">{t.sub}</div>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { loading, error, data, filter, search, sortKey, sortDir } = this.state;
        if (loading) return <div className="fin-loading">Loading salons…</div>;
        if (error) return <div className="fin-error">{error}</div>;
        if (!data) return null;

        const rows = this.getFilteredSorted(data.salons || [], filter, search);

        const header = (key, label, cls = '') => (
            <th
                className={cls}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => this.setSort(key)}
            >
                {label}
                {sortKey === key && <span style={{ marginLeft: 4 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>}
            </th>
        );

        return (
            <div>
                {this.renderSummary(data.summary, data.total)}

                <div className="fin-section">
                    <div className="fin-section-header">
                        <h3 className="fin-section-title">Salon pipeline</h3>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                                type="text"
                                placeholder="Search salon name"
                                value={search}
                                onChange={e => this.setState({ search: e.target.value })}
                                style={{
                                    padding: '6px 10px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    width: 200
                                }}
                            />
                            <button
                                onClick={this.exportCsv}
                                style={{
                                    padding: '6px 12px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    background: '#fff',
                                    color: '#007EA0',
                                    cursor: 'pointer'
                                }}
                            >
                                Export CSV
                            </button>
                        </div>
                    </div>

                    {/* Filter chips */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                        {FILTER_ORDER.map(f => {
                            const label = f === 'all'
                                ? `All (${fmtInt(data.total)})`
                                : `${STATUS_META[f].label} (${fmtInt(data.summary?.[f] || 0)})`;
                            const active = filter === f;
                            return (
                                <button
                                    key={f}
                                    onClick={() => this.setState({ filter: f })}
                                    style={{
                                        padding: '5px 12px',
                                        borderRadius: 999,
                                        fontSize: 12,
                                        border: active ? '1px solid #007EA0' : '1px solid #e5e7eb',
                                        background: active ? '#007EA0' : '#fff',
                                        color: active ? '#fff' : '#737373',
                                        cursor: 'pointer',
                                        fontWeight: active ? 600 : 500
                                    }}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>

                    <table className="fin-table">
                        <thead>
                            <tr>
                                {header('salonName', 'Salon')}
                                {header('status', 'Status')}
                                {header('createdTime', 'Signed up')}
                                {header('ageDays', 'Age', 'num')}
                                {header('hairdresserCount', 'Stylists', 'num')}
                                {header('totalBookings', 'Bookings', 'num')}
                                {header('bookingsLast30', 'Last 30d', 'num')}
                                {header('lastBooking', 'Last booking')}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={8} className="fin-dim" style={{ padding: '20px', textAlign: 'center' }}>No salons match these filters.</td></tr>
                            )}
                            {rows.map(r => {
                                const meta = STATUS_META[r.status] || {};
                                return (
                                    <tr key={r.salonId}>
                                        <td>{r.salonName}</td>
                                        <td>
                                            <span className={`fin-pill ${meta.tone === 'warn' ? 'warn' : meta.tone === 'ok' ? 'ok' : ''}`}>
                                                {meta.label || r.status}
                                            </span>
                                        </td>
                                        <td>{fmtDate(r.createdTime)}</td>
                                        <td className="num">{r.ageDays != null ? `${r.ageDays}d` : '—'}</td>
                                        <td className="num">{fmtInt(r.hairdresserCount)}</td>
                                        <td className="num">{fmtInt(r.totalBookings)}</td>
                                        <td className="num">{fmtInt(r.bookingsLast30)}</td>
                                        <td>
                                            {r.lastBooking ? (
                                                <span title={fmtDate(r.lastBooking)}>{daysAgo(r.lastBooking)}</span>
                                            ) : <span className="fin-dim">never</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export default SalonsView;
